'use client';
import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface CheckInItem {
  id: string;
  name: string;
  sport: string;
  time: string;
  court: string;
  avatar: string;
}

const sportColors: Record<string, string> = {
  Badminton: 'bg-primary/10 text-primary',
  Gym: 'bg-accent/10 text-accent',
  'Box Cricket': 'bg-success-bg text-success',
  Zumba: 'bg-warning-bg text-warning',
  Volleyball: 'bg-info-bg text-info',
  Archery: 'bg-secondary text-secondary-foreground',
};

function getTodayStr(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export default function RecentCheckIns() {
  const [checkIns, setCheckIns] = useState<CheckInItem[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    async function load() {
      try {
        const supabase = createClient();
        const today = getTodayStr();
        const { data } = await supabase
          .from('attendance')
          .select('id, member_name, sport, court, check_in_time')
          .eq('date', today)
          .order('check_in_time', { ascending: false })
          .limit(7);

        const { count } = await supabase
          .from('attendance')
          .select('id', { count: 'exact', head: true })
          .eq('date', today);

        setTotal(count ?? 0);
        setCheckIns(
          (data ?? []).map((r) => ({
            id: r.id,
            name: r.member_name ?? '',
            sport: r.sport ?? '',
            time: r.check_in_time ?? '',
            court: r.court ?? '',
            avatar: (r.member_name ?? 'XX').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase(),
          }))
        );
      } catch {
        setCheckIns([]);
      }
    }
    load();
  }, []);

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[15px] font-semibold text-foreground">Recent Check-ins</h3>
          <p className="text-[12px] text-muted-foreground">Today's activity — {total} total</p>
        </div>
        <Link href="/attendance-check-ins" className="flex items-center gap-1 text-[12px] text-primary font-medium hover:underline">
          View all <ArrowRight size={12} />
        </Link>
      </div>
      {checkIns.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-[13px] text-muted-foreground">
          No check-ins today
        </div>
      ) : (
        <ul className="space-y-2">
          {checkIns.map((item) => (
            <li key={item.id} className="flex items-center gap-3 py-1.5 hover:bg-muted/50 rounded-lg px-1 transition-colors">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-primary">{item.avatar}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-foreground truncate">{item.name}</p>
                <p className="text-[11px] text-muted-foreground">{item.court}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${sportColors[item.sport] ?? 'bg-secondary text-secondary-foreground'}`}>
                  {item.sport}
                </span>
                <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">{item.time}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}